/**
 * Les 12 groupes (specs/01 §4) — cartes style legacy, classement moteur FIFA.
 * Lignes colorées : 1er/2e qualifiés, 3e repêchable (avec son rang parmi les
 * 3es), éliminé. Score live intégré => classement « provisoire » badgé ⏱.
 *
 * Expose aussi de quoi animer le replay d'ouverture (squelette + FLIP des
 * lignes groupe par groupe) — logique reprise du legacy.
 */
import type { OfficialState } from "../../app/officialStore.ts";
import type { RankedTeam } from "../../engine/standings.ts";
import type { TeamCode } from "../../engine/types.ts";
import { esc, flagSpan } from "../html.ts";
import { fmtSigned } from "../../app/format.ts";
import { t } from "../../app/i18n.ts";

const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

interface RowLike {
  readonly team: TeamCode;
  readonly rank: number;
  readonly points: number;
  readonly goalDiff: number;
}

/**
 * Couleur de qualification. On ne colore QU'UNE FOIS LE GROUPE JOUÉ EN ENTIER
 * (`complete`) : le vert « qualifié » ne doit pas apparaître sur un classement
 * provisoire, seulement quand c'est SÛR (demande utilisateur). Le tri des lignes
 * reste provisoire — seule la couleur attend la certitude.
 */
export function colorClass(rank: number, complete: boolean, thirdRank: number | undefined): string {
  if (!complete) return "";
  if (rank <= 2) return "q";
  if (rank === 3) return thirdRank === undefined || thirdRank <= 8 ? "q3" : "out";
  return "out";
}

function rowHtml(
  r: RowLike,
  complete: boolean,
  thirdRank: number | undefined,
  provisional: boolean,
): string {
  const cls = colorClass(r.rank, complete, thirdRank);
  const thirdBadge =
    r.rank === 3 && thirdRank !== undefined
      ? `<span class="third-rank ${thirdRank <= 8 ? "in" : "out"}" title="${t("groups.thirdRankTitle")}">${t("groups.thirdRankBadge", { n: thirdRank })}</span>`
      : "";
  return `
    <div class="gr ${cls} ${provisional ? "prov" : ""}" data-t="${esc(r.team)}">
      <span class="gp-rank">${r.rank}</span>
      ${flagSpan(r.team)}
      ${provisional ? `<span class="badge-prov" title="${t("groups.liveScoreTitle")}" aria-label="${t("groups.liveScoreTitle")}"><i class="ti ti-clock" aria-hidden="true"></i></span>` : ""}
      ${thirdBadge}
      <span class="gd">${fmtSigned(r.goalDiff)}</span>
      <span class="gp">${r.points}</span>
    </div>`;
}

function cardHtml(
  group: string,
  rows: readonly RowLike[],
  opts: {
    complete: boolean;
    provisional: ReadonlySet<TeamCode>;
    thirdsRank: Map<TeamCode, number>;
  },
): string {
  const played = Math.round(
    rows.reduce((acc, r) => acc + ((r as { played?: number }).played ?? 0), 0) / 2,
  );
  return `
    <div class="gc ${opts.complete ? "done" : ""}" id="gc${group}">
      <div class="gh"><span>${t("stats.group.cell", { group })}</span><span class="ghk">${
        opts.provisional.size
          ? `<span class="badge-prov" title="${t("groups.provisionalTitle")}" aria-label="${t("groups.provisionalTitle")}"><i class="ti ti-clock" aria-hidden="true"></i></span> `
          : ""
      }${t("groups.playedCount", { played })}</span></div>
      <div class="grows">
        ${rows.map((r) => rowHtml(r, opts.complete, opts.thirdsRank.get(r.team), opts.provisional.has(r.team))).join("")}
      </div>
    </div>`;
}

const legend = (): string =>
  `<p class="note legend"><span class="lg-sw dir" aria-hidden="true"></span> ${t("groups.legend.direct")} · <span class="lg-sw rep" aria-hidden="true"></span> ${t("groups.legend.thirds")} · <i class="ti ti-clock" aria-hidden="true"></i> ${t("groups.legend.live")}</p>`;

export function renderGroupsGrid(el: HTMLElement, state: OfficialState): void {
  const thirdsRank = new Map(state.thirds.map((t, i) => [t.team, i + 1]));
  el.innerHTML = `
    <h2 class="sec-title">${t("stats.section.groups")}</h2>
    <div class="gzone">
      ${state.standings
        .map((s) =>
          cardHtml(s.group, s.rows, {
            complete: s.complete,
            provisional: s.provisionalTeams,
            thirdsRank,
          }),
        )
        .join("")}
    </div>
    ${legend()}`;
}

// --- Replay helpers --------------------------------------------------------

/** Initial grid: every team at 0, alphabetical, no colour (replay start). */
export function renderGroupsSkeleton(
  el: HTMLElement,
  groups: Readonly<Record<string, readonly TeamCode[]>>,
): void {
  const empty = new Set<TeamCode>();
  const noThirds = new Map<TeamCode, number>();
  el.innerHTML = `
    <h2 class="sec-title">${t("stats.section.groups")}</h2>
    <div class="gzone">
      ${Object.keys(groups)
        .sort()
        .map((g) => {
          const rows = (groups[g] ?? []).map((team, i) => ({
            team,
            rank: i + 1,
            points: 0,
            goalDiff: 0,
            played: 0,
          }));
          return cardHtml(g, rows, { complete: false, provisional: empty, thirdsRank: noThirds });
        })
        .join("")}
    </div>
    ${legend()}`;
}

/** Re-render one group's rows in `rows` order, animating the move (FLIP). */
export function flipGroup(
  el: HTMLElement,
  group: string,
  rows: readonly RankedTeam[],
  highlight: readonly TeamCode[] = [],
): void {
  const cont = el.querySelector<HTMLElement>(`#gc${group} .grows`);
  if (!cont) return;
  const prevTop = new Map<string, number>();
  cont.querySelectorAll<HTMLElement>(".gr").forEach((r) => {
    if (r.dataset.t) prevTop.set(r.dataset.t, r.getBoundingClientRect().top);
  });
  // Vert « qualifié » seulement quand le groupe est JOUÉ EN ENTIER (chaque
  // équipe a disputé ses 3 matchs) — pas sur un classement provisoire.
  const complete = rows.length > 0 && rows.every((r) => r.played === 3);
  cont.innerHTML = rows.map((r) => rowHtml(r, complete, undefined, false)).join("");
  const hl = new Set(highlight);
  cont.querySelectorAll<HTMLElement>(".gr").forEach((r) => {
    const code = r.dataset.t;
    if (!code) return;
    if (hl.has(code)) r.classList.add("flash");
    if (RM) return;
    const old = prevTop.get(code);
    if (old === undefined) return;
    const dy = old - r.getBoundingClientRect().top;
    if (dy) {
      r.style.transition = "none";
      r.style.transform = `translateY(${dy}px)`;
      requestAnimationFrame(() => {
        r.style.transition = "transform .45s cubic-bezier(.4,0,.2,1)";
        r.style.transform = "";
      });
    }
  });
}

/**
 * Morph d'un groupe vers `rows` : réordonne les lignes ET anime leur DÉPLACEMENT
 * (FLIP) vers la nouvelle position, via un reflow synchrone — robuste même hors
 * rAF (onglet masqué). Pas de flash (≠ flipGroup) : sert le « fondu » d'ouverture
 * où l'on va DIRECTEMENT à la situation actuelle du classement, sans rejouer les
 * étapes. Sous mouvement réduit : réordonne sans animer.
 */
export function morphGroupTo(el: HTMLElement, group: string, rows: readonly RankedTeam[]): void {
  const cont = el.querySelector<HTMLElement>(`#gc${group} .grows`);
  if (!cont) return;
  const prevTop = new Map<string, number>();
  cont.querySelectorAll<HTMLElement>(".gr").forEach((r) => {
    if (r.dataset.t) prevTop.set(r.dataset.t, r.getBoundingClientRect().top);
  });
  const complete = rows.length > 0 && rows.every((r) => r.played === 3);
  cont.innerHTML = rows.map((r) => rowHtml(r, complete, undefined, false)).join("");
  if (RM) return;
  const moved: HTMLElement[] = [];
  cont.querySelectorAll<HTMLElement>(".gr").forEach((r) => {
    const code = r.dataset.t;
    const old = code ? prevTop.get(code) : undefined;
    const dy = old === undefined ? 0 : old - r.getBoundingClientRect().top;
    if (!dy) return;
    r.style.transition = "none";
    r.style.transform = `translateY(${dy}px)`;
    moved.push(r);
  });
  if (!moved.length) return;
  void cont.offsetWidth; // reflow : COMMIT les positions de départ (sans rAF)
  for (const r of moved) {
    r.style.transition = "transform .55s cubic-bezier(.22,1,.36,1)";
    r.style.transform = "";
    const clear = (): void => {
      r.style.transition = "";
      r.style.transform = "";
    };
    r.addEventListener("transitionend", clear, { once: true });
    setTimeout(clear, 700); // filet : jamais coincé en translateY (onglet masqué)
  }
}

/** Update the "played/6 · pts" header of a group during replay. */
export function setGroupHeaderPlayed(el: HTMLElement, group: string, played: number): void {
  const ghk = el.querySelector<HTMLElement>(`#gc${group} .ghk`);
  if (ghk) ghk.textContent = t("groups.playedCount", { played });
}
